import posthog from "posthog-js";
import type { User } from "../models/Entities";

/**
 * Identify the current user to PostHog using the same stable identifier
 * (user.uuid) that Sentry already keys on in this repo.
 *
 * Person and group properties are limited to slowly-changing attributes that
 * are useful as filter dimensions for funnel and cohort analysis. Behavioral
 * state (onboarding completion, linked-account counts, first-activation date)
 * belongs in events, not properties — PostHog builds those cohorts and funnels
 * from raw events, which avoids the snapshot-at-identify staleness that
 * person-on-events mode would otherwise bake into every event.
 */
export function identifyPostHogUser(user: User): void {
  try {
    // The Organization interface in Entities.tsx does not declare
    // organization_billing_plan; it is attached at runtime. Pull it via a
    // narrowed cast to avoid duplicating the AppContext shim type.
    const orgWithBilling = user.organization as User["organization"] & {
      organization_billing_plan?: { billing_plan?: { plan_tier?: string } };
    };
    const planTier = orgWithBilling.organization_billing_plan?.billing_plan?.plan_tier ?? null;
    const emailDomain = user.email.split("@")[1]?.toLowerCase() ?? null;

    posthog.identify(user.uuid, {
      role: user.type,
      plan_tier: planTier,
      signup_date: user.terms_agreement_datetime,
      // For filtering internal Merge accounts out of customer-facing dashboards.
      is_staff: user.is_staff,
      email_domain: emailDomain,
    });

    posthog.group("organization", user.organization.id, {
      name: user.organization.name,
      plan_tier: planTier,
    });
  } catch (err) {
    console.error("PostHog identify failed:", err);
  }
}

/**
 * Clear PostHog identity on logout / user-switch.
 *
 * posthog.reset() clears ALL super properties, not just the identity, and the
 * loaded callback does not re-fire afterwards. The product: "unified" super
 * property registered in posthogOptions.loaded is therefore lost for the rest
 * of the page session unless we re-register it here. Any events captured after
 * reset and before a full page reload would otherwise leak through without it.
 *
 * Matches the pattern used in agent-handler-frontend and merge-gateway-control-frontend.
 */
export function clearPostHogIdentity(): void {
  try {
    posthog.reset();
    posthog.register({ product: "unified" });
  } catch (err) {
    console.error("PostHog reset failed:", err);
  }
}
